// Sugalarm — Household dashboard non-active member card (cycle 14).
//
// Tappable. Calls `onPickActive(member_id)` on click. Shows the
// member's avatar + name, big glucose number, trend arrow, and a
// zone pill when the member is out of range. Greys when stale,
// "—" with "No reading yet" when the member has no reading.

import { TrendArrow } from "../../components/glucose/TrendArrow";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import { Pill } from "../../components/ui/Pill";
import type { CurrentGlucoseView } from "../../lib/currentGlucose";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneLabel,
  zoneOf,
  zoneTone,
} from "../../lib/glucose";
import type { AlertLevel, Thresholds, Zone } from "../../lib/types";
import { followerStoppedLine } from "../../lib/serviceLock";
import styles from "./MemberCard.module.css";

interface Props {
  memberId: string;
  displayName: string;
  /** Cycle 105: member avatar id (null → Sugalarm shield). */
  avatarId?: string | null;
  relationshipLabel: string | null;
  isSelf: boolean;
  cgmProvider: string | null;
  /** ⚠ Cycle 435: the selector's RESULT for this member
   *  (`lib/currentGlucose.ts`); the number is painted only from
   *  `view.displayValue`. A raw `Reading` cannot be passed here. */
  view: CurrentGlucoseView;
  /** Surfaced from the dashboard seed; nullable. */
  currentAlertLevel: AlertLevel | null;
  units: "mg/dL" | "mmol/L";
  /** Cycle 35: live thresholds threaded from HouseholdScreen via
   *  `useThresholds()`. Required so zone mapping respects the user's
   *  configured low/high boundaries. */
  thresholds: Thresholds;
  onPickActive: (memberId: string) => void;
  /** Cycle 169: frosted-glass fill — set only on the mobile frame so the
   *  aurora glows through. Kiosk/web omit it and keep the solid card; the
   *  urgent pulse card also stays solid (its keyframe owns the fill). */
  glass?: boolean;
  /** Cycle 468: the member's owner is locked out; the stopped line replaces
   *  readings and No Data, the number is withheld. */
  serviceLocked?: boolean;
}

function providerShort(provider: string | null): string {
  if (!provider) return "No CGM";
  if (provider === "dexcom") return "Dexcom";
  if (provider === "libre") return "Libre";
  return provider[0]!.toUpperCase() + provider.slice(1);
}

export function MemberCard({
  memberId,
  displayName,
  avatarId = null,
  relationshipLabel,
  isSelf,
  cgmProvider,
  view,
  currentAlertLevel,
  units,
  thresholds,
  onPickActive,
  glass = false,
  serviceLocked = false,
}: Props) {
  // Cycle 435: one fact from the selector — released as current, or not.
  // The card's existing No Data treatment is reused; the number is
  // withheld rather than greyed, and the age label keeps saying how old
  // the last value is.
  const current = view.displayValue;
  const hasReading = view.reading != null;
  const stale = current == null;
  // The pill's zone: the current value's zone, or — when the number is
  // withheld — the SERVER's own alert level for the member (regression-
  // guard: the first draft collapsed an open urgent low's pill to a
  // neutral "—"; before this cycle it was the stale sample's zone).
  const zone: Zone = current
    ? zoneOf(current.sgv, thresholds)
    : (currentAlertLevel ?? "unknown");
  const ageMin = view.ageMinutes ?? 0;
  const trend = trendInfo(current?.trend ?? null, current?.trendRate ?? null);
  const tone = zoneTone(zone);

  // Show a zone pill only when the member is out of range (or has an
  // active alert). In-range stays clean.
  const showZonePill =
    zone === "low" ||
    zone === "high" ||
    zone === "urgent_low" ||
    zone === "urgent_high" ||
    currentAlertLevel != null;

  const pulse =
    !stale &&
    (zone === "urgent_low" ||
      zone === "urgent_high" ||
      currentAlertLevel === "urgent_low" ||
      currentAlertLevel === "urgent_high");

  return (
    <button
      type="button"
      className={`${styles.card} ${pulse ? styles.cardUrgent : ""} ${
        glass && !pulse ? styles.glass : ""
      }`}
      onClick={() => onPickActive(memberId)}
      title={`Show ${displayName} on the Now screen`}
    >
      <div className={styles.head}>
        <MemberAvatar avatarId={avatarId} name={displayName} size={36} />
        <div className={styles.meta}>
          <div className={styles.name}>{displayName}</div>
          <div className={styles.rel}>
            {isSelf ? "YOU" : (relationshipLabel || "MEMBER").toUpperCase()}
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div
          className={styles.value}
          style={{ opacity: !hasReading ? 0.45 : stale ? 0.55 : 1 }}
        >
          {current && !serviceLocked ? displayValue(current.sgv, units) : "—"}
        </div>
        <div className={styles.trend}>
          <TrendArrow
            glyph={current && !serviceLocked ? trend.glyph : "—"}
            zone={zone}
            stale={stale}
            size={28}
          />
        </div>
      </div>

      {serviceLocked ? (
        <p className={styles.stopped} data-testid="member-stopped-line">
          {followerStoppedLine(displayName)}
        </p>
      ) : null}

      <div className={styles.foot}>
        <span className={styles.footProvider}>{providerShort(cgmProvider)}</span>
        <span className={styles.footDot} />
        <span className={styles.footAge}>
          {serviceLocked ? "Monitoring stopped" : !hasReading ? "No reading yet" : fmtAge(ageMin)}
        </span>
        {showZonePill && !serviceLocked ? (
          <span className={styles.zonePillWrap}>
            <Pill tone={tone} appearance="status">{zoneLabel(zone)}</Pill>
          </span>
        ) : null}
      </div>

      {/* Cycle 162.1: explicit activation affordance. The whole card has
          always been the tap target (onPickActive above) — this pill just
          SAYS so. Presentational only; the grid holds non-active members
          exclusively, so it renders on every card. */}
      <div className={styles.activateRow}>
        <span className={styles.activatePill}>Tap to activate ›</span>
      </div>
    </button>
  );
}
